import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Crown, Calendar, ArrowRight, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chrome background
// Default gradient background (no hardcoded images)
const DEFAULT_GRADIENT = "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)";

// Helper to get image URL
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API}${path}`;
};

// Timezone-safe date formatting
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split('T')[0].split('-');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { 
    weekday: "long", 
    month: "long", 
    day: "numeric", 
    year: "numeric" 
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const VipClaimPage = () => {
  const { claimToken } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [claimData, setClaimData] = useState(null);

  useEffect(() => {
    if (claimToken) {
      validateToken();
    }
  }, [claimToken]);

  const validateToken = async () => {
    try {
      setStatus("loading");
      const res = await fetch(`${API}/vip/claim/${claimToken}`);
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Invalid invitation");
      }
      
      const data = await res.json();
      setClaimData(data);
      setStatus("valid");
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  const claimSpot = async () => {
    if (!isAuthenticated) {
      localStorage.setItem("join_redirect", `/vip/claim/${claimToken}`);
      navigate("/login");
      return;
    }

    try {
      setStatus("claiming");
      const sessionToken = localStorage.getItem("session_token");
      
      const res = await fetch(`${API}/vip/claim/${claimToken}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`
        }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to claim VIP spot");
      }
      
      const data = await res.json();
      setStatus("success");
      toast.success("You're on the VIP list!");
      
      setTimeout(() => {
        navigate(data.redirect_url || `/confirmation/${data.rsvp_id}`);
      }, 2000);
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: DEFAULT_GRADIENT }}
    >
      <div className="max-w-md w-full">
        
        {/* Loading */}
        {status === "loading" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-yellow-600 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Verifying VIP invite...</h1>
          </Card>
        )}

        {/* Valid Invitation */}
        {status === "valid" && claimData && (
          <Card className="overflow-hidden">
            {/* Event Cover - Use event cover or gradient (no hardcoded images) */}
            <div 
              className="relative aspect-square w-full max-h-48 overflow-hidden"
              style={claimData.event?.cover_image 
                ? { backgroundImage: `url(${claimData.event.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                : { background: DEFAULT_GRADIENT }
              }
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              
              {/* VIP Badge */}
              <div className="absolute top-3 right-3">
                <Badge className="bg-yellow-500 text-white px-3 py-1">
                  <Crown className="w-4 h-4 mr-1" />
                  VIP
                </Badge>
              </div>
              
              {/* Logo */}
              {claimData.event?.logo_image && (
                <div className="absolute bottom-3 left-3">
                  <div className="w-12 h-12 rounded-lg bg-white p-1 shadow-lg">
                    <img 
                      src={getImageUrl(claimData.event.logo_image)} 
                      alt="" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <CardContent className="p-6 space-y-5">
              {/* Header */}
              <div className="text-center space-y-2">
                <div className="w-12 h-12 mx-auto bg-yellow-100 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-yellow-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">
                  You're Invited as VIP!
                </h1>
                <p className="text-gray-600 text-sm">
                  {claimData.list_host_name && (
                    <><span className="font-medium">{claimData.list_host_name}</span> has added you to their VIP list</>
                  )}
                </p>
              </div>

              {/* Event Info */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 space-y-3 border border-yellow-200">
                <h2 className="font-semibold text-lg text-gray-900">{claimData.event?.name}</h2>
                
                {claimData.event?.date && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {formatDate(claimData.event.date)}
                      {claimData.event.start_time && (
                        <span className="ml-1">
                          • {formatTime(claimData.event.start_time)}
                          {claimData.event.timezone_abbr && ` ${claimData.event.timezone_abbr}`}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                
                {(claimData.event?.venue_name || claimData.event?.city) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {claimData.event.venue_name}
                      {claimData.event.venue_name && claimData.event.city && ", "}
                      {claimData.event.city}
                    </span>
                  </div>
                )}
              </div>

              {/* VIP List Info */}
              {claimData.list_name && (
                <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <Crown className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">{claimData.list_name}</p>
                    <p className="text-xs text-gray-500">VIP List</p>
                  </div>
                </div>
              )}

              {/* Reserved Name */}
              {claimData.member_name && (
                <div className="text-center text-sm text-gray-500">
                  Reserved for: <span className="font-medium">{claimData.member_name}</span>
                </div>
              )}

              {/* CTA */}
              <Button 
                onClick={claimSpot}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3"
                data-testid="claim-vip-btn"
              >
                {isAuthenticated ? (
                  <>
                    Claim Your VIP Spot
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Sign In to Claim
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Claiming */}
        {status === "claiming" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-yellow-600 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Claiming your VIP spot...</h1>
          </Card>
        )}

        {/* Success */}
        {status === "success" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">You're on the VIP list!</h1>
            <p className="text-gray-600 mt-2">Redirecting to your confirmation...</p>
          </Card>
        )}

        {/* Error */}
        {status === "error" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">VIP Invite Error</h1>
            <p className="text-gray-600 mt-2">{error || "This VIP invite link is invalid or has already been claimed."}</p>
            <Button 
              onClick={() => navigate("/")}
              variant="outline"
              className="mt-4"
            >
              Go Home
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default VipClaimPage;
